import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Avatar,
  LinearProgress,
  Tabs,
  Tab,
  Button,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Container,
  useTheme,
  useMediaQuery,
  IconButton
} from '@mui/material';
import {
  EmojiEvents,
  Star,
  LocalFireDepartment,
  TrendingUp,
  WorkspacePremium,
  Timeline,
  Share,
  Refresh,
  Lock,
  LockOpen,
  Settings,
  ChevronLeft,
  ChevronRight
} from '@mui/icons-material';
import { getCurrentUser } from 'aws-amplify/auth';
import { achievementService } from '../../services/achievementService';
import { adminAchievementService } from '../../services/adminAchievementService';
import { useAuth } from '../../contexts/AuthContext';
import AchievementSettings from './AchievementSettings';

const RARITY_COLORS = {
  common: '#9E9E9E',
  uncommon: '#4CAF50', 
  rare: '#2196F3',
  epic: '#9C27B0',
  legendary: '#FFD700'
};

const UserAchievementsDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { refreshUser, updateUserProfile } = useAuth();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [achievements, setAchievements] = useState([]);
  const [badges, setBadges] = useState([]);
  const [allAchievements, setAllAchievements] = useState([]);
  const [allBadges, setAllBadges] = useState([]);
  const [allTitles, setAllTitles] = useState([]);
  const [userStats, setUserStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [distinctMoviesWatched, setDistinctMoviesWatched] = useState(0);

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      setLoading(true);
      const currentUser = await getCurrentUser();
      
      // Get full user data from database
      if (currentUser?.userId) {
        const fullUserData = await achievementService.getUserStats(currentUser.userId);
         const enhancedUser = {
          ...currentUser,
          id: currentUser.userId,
          titles: fullUserData.titles || [],
          badgeDisplaySettings: fullUserData.badgeDisplaySettings,
          titleOrder: fullUserData.titleOrder || []
        };
        setUser(enhancedUser);
        
        await Promise.all([
          loadAchievements(currentUser.userId),
          loadBadges(currentUser.userId),
          loadUserStats(currentUser.userId),
          loadAllDefinitions()
        ]);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAllDefinitions = async () => {
    try {
      const [achievementDefs, badgeDefs, titleDefs] = await Promise.all([
        adminAchievementService.getAllAchievements(),
        adminAchievementService.getAllBadges(),
        adminAchievementService.getAllTitles()
      ]);
      setAllAchievements(achievementDefs);
      setAllBadges(badgeDefs);
      setAllTitles(titleDefs);
    } catch (error) {
      console.error('Error loading definitions:', error);
    }
  };

  const loadAchievements = async (userId) => {
    try {
      const userAchievements = await achievementService.getUserAchievements(userId);
      setAchievements(userAchievements);
    } catch (error) {
      console.error('Error loading achievements:', error);
    }
  };

  const loadBadges = async (userId) => {
    try {
      const userBadges = await achievementService.getUserBadges(userId);
      setBadges(userBadges);
    } catch (error) {
      console.error('Error loading badges:', error);
    }
  };

  const loadUserStats = async (userId) => {
    try {
      const stats = await achievementService.getUserStats(userId);
      setUserStats(stats);
      setDistinctMoviesWatched(stats.moviesWatched || 0);
    } catch (error) {
      console.error('Error loading user stats:', error);
    }
  };

  const handleRecalculate = async () => {
    if (!user?.userId) return;
    
    try {
      setRecalculating(true);
      await achievementService.recalculateUserAchievements(user.userId);
      await loadUserData(); // Refresh all data
    } catch (error) {
      console.error('Error recalculating achievements:', error);
    } finally {
      setRecalculating(false);
    }
  };

  const unlockedAchievements = achievements.filter(a => a.unlocked);
  
  // Use totalAchievementPoints from User table (includes ACTION_BASED + unlock points)
  const totalPoints = userStats.totalAchievementPoints || 0;
  
  // Get current title type
  const currentTitleType = (Array.isArray(user?.titles) && user.titles.length > 0) 
    ? user.titles[0] 
    : 'newcomer';
  
  // Find title definition to get the display name
  const currentTitleDef = allTitles.find(t => t.id === currentTitleType || t.type === currentTitleType);
  const currentTitle = currentTitleDef?.name || 'Newcomer';

  const StatCard = ({ icon: Icon, title, value, color = 'primary' }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ 
        textAlign: 'center', 
        py: isMobile ? 1.5 : 2,
        px: isMobile ? 1 : 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
      }}>
        <Icon color={color} sx={{ fontSize: isMobile ? 32 : 40, mb: 1 }} />
        <Typography variant={isMobile ? "h5" : "h4"} fontWeight={600}>{value}</Typography>
        <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary">{title}</Typography>
      </CardContent>
    </Card>
  );

  const handleAchievementClick = (achievement) => {
    const event = new CustomEvent('achievementUnlocked', { 
      detail: {
        name: achievement.achievementName || achievement.name,
        description: achievement.description,
        points: achievement.points,
        rarity: achievement.rarity,
        achievementType: achievement.achievementType
      }
    });
    window.dispatchEvent(event);
  };

  const handleBadgeClick = (badge) => {
    const event = new CustomEvent('badgeEarned', { 
      detail: {
        name: badge.name,
        description: badge.description,
        points: badge.points,
        rarity: badge.rarity,
        color: badge.color,
        icon: badge.icon,
        badgeType: badge.type
      }
    });
    window.dispatchEvent(event);
  };

  const AchievementCard = ({ achievement }) => (
    <Card sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
      <CardContent>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <EmojiEvents />
          </Avatar>
          <Box flex={1}>
            <Typography variant="h6">{achievement.achievementName || achievement.achievementType}</Typography>
            <Typography variant="body2" color="text.secondary">
              Unlocked {new Date(achievement.unlockedAt).toLocaleDateString()}
            </Typography>
          </Box>
          <Button size="small" startIcon={<Share />}>Share</Button>
        </Box>
      </CardContent>
    </Card>
  );

  const BadgeCard = ({ badge, isUnlocked = true }) => (
    <Card 
      onClick={() => isUnlocked && handleBadgeClick(badge)}
      sx={{ 
        textAlign: 'center', 
        p: isMobile ? 1.5 : 2,
        opacity: isUnlocked ? 1 : 0.5,
        border: isUnlocked ? 'none' : '2px dashed #ccc',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        cursor: isUnlocked ? 'pointer' : 'default',
        '&:hover': isUnlocked ? { transform: 'scale(1.05)', transition: 'transform 0.2s' } : {}
      }}>
      <Box>
        <Avatar 
          sx={{ 
            bgcolor: isUnlocked ? (badge.color || RARITY_COLORS[badge.rarity] || 'primary.main') : '#ccc',
            width: isMobile ? 48 : 60,
            height: isMobile ? 48 : 60,
            mx: 'auto',
            mb: 1
          }}
        >
          {isUnlocked ? <Star /> : <Lock />}
        </Avatar>
        <Typography variant={isMobile ? "body2" : "subtitle2"} fontWeight={600} sx={{ mb: 0.5 }}>
          {badge.name}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {badge.rarity} {!isUnlocked && '• Locked'}
        </Typography>
      </Box>
      <Chip 
        label={`${badge.points || 0} pts`} 
        size="small" 
        sx={{ alignSelf: 'center' }}
      />
    </Card>
  );

  const AchievementProgressCard = ({ achievement, userProgress }) => {
    const isUnlocked = userProgress && userProgress.unlocked;
    const hideDescription = !isUnlocked && (achievement.rarity === 'epic' || achievement.rarity === 'legendary');
    
    return (
      <Card 
        onClick={() => isUnlocked && handleAchievementClick({ ...achievement, ...userProgress })}
        sx={{ 
          mb: 2, 
          border: '1px solid', 
          borderColor: isUnlocked ? 'success.main' : 'divider',
          opacity: isUnlocked ? 1 : 0.7,
          cursor: isUnlocked ? 'pointer' : 'default',
          '&:hover': isUnlocked ? { transform: 'scale(1.01)', transition: 'transform 0.2s' } : {}
        }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ bgcolor: isUnlocked ? 'success.main' : 'grey.500' }}>
              {isUnlocked ? <LockOpen /> : <Lock />}
            </Avatar>
            <Box flex={1}>
              <Typography variant="h6">{achievement.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {hideDescription ? '???' : achievement.description}
              </Typography>
              {isUnlocked && userProgress.unlockedAt && (
                <Typography variant="caption" color="success.main">
                  Unlocked {new Date(userProgress.unlockedAt).toLocaleDateString()}
                </Typography>
              )}
            </Box>
            <Chip 
              label={`${achievement.points} pts`}
              color={isUnlocked ? 'success' : 'default'}
              size="small"
            />
          </Box>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Alert severity="info">
        Please sign in to view your achievements and progress.
      </Alert>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Header */}
      <Box mb={3}>
        <Box mb={2}>
          <Typography variant={isMobile ? "h5" : "h4"} fontWeight={600}>
            🏆 My Achievements
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track your progress and unlock new achievements
          </Typography>
        </Box>
        <Box sx={{ 
          display: 'flex', 
          gap: 1, 
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center'
        }}>
          <Button
            variant="outlined"
            startIcon={<Settings />}
            onClick={() => setSettingsOpen(true)}
            size={isMobile ? "medium" : "medium"}
            fullWidth={isMobile}
          >
            Customize
          </Button>
          <Button
            variant="outlined"
            startIcon={recalculating ? <CircularProgress size={16} /> : <Refresh />}
            onClick={handleRecalculate}
            disabled={recalculating}
            size={isMobile ? "medium" : "medium"}
            fullWidth={isMobile}
          >
            {recalculating ? 'Updating...' : 'Refresh Progress'}
          </Button>
        </Box>
      </Box>

      {/* Current Title */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <CardContent sx={{ color: 'white', textAlign: 'center' }}>
          <WorkspacePremium sx={{ fontSize: 48, mb: 1 }} />
          <Typography variant="h5" fontWeight={600}>{currentTitle}</Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Your current title • {totalPoints} total points
          </Typography>
        </CardContent>
      </Card>

      {/* Achievement Points Explanation */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <CardContent sx={{ color: 'white' }}>
              <Box display="flex" alignItems="center" gap={2} mb={1}>
                <EmojiEvents sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" fontWeight={600}>
                    {userStats.totalAchievementPoints || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Lifetime Points
                  </Typography>
                </Box>
              </Box>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                🏆 Earned from unlocking achievements • Permanent & never resets • Used for badges & titles
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
            <CardContent sx={{ color: 'white' }}>
              <Box display="flex" alignItems="center" gap={2} mb={1}>
                <TrendingUp sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" fontWeight={600}>
                    {userStats.leaderboardPoints || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Season Points ({userStats.currentLeaderboardPeriod || 'Current'})
                  </Typography>
                </Box>
              </Box>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                🔥 Earned from daily actions (watch movies, write reviews) • Resets {userStats.currentLeaderboardPeriod ? 'quarterly' : 'periodically'} • Used for leaderboard ranking
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Stats Overview */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <StatCard 
            icon={EmojiEvents} 
            title="Achievements" 
            value={unlockedAchievements.length}
            color="primary"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard 
            icon={Star} 
            title="Badges" 
            value={badges.length}
            color="secondary"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard 
            icon={LocalFireDepartment} 
            title="Login Streak" 
            value={userStats.loginStreak || 0}
            color="error"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard 
            icon={TrendingUp} 
            title="Early Discoveries" 
            value={userStats.earlyDiscoveries || 0}
            color="success"
          />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons={isMobile ? "auto" : false}
          allowScrollButtonsMobile
          sx={{
            '& .MuiTabs-scrollButtons': {
              '&.Mui-disabled': { opacity: 0.3 }
            }
          }}
        >
          <Tab label={isMobile ? "Achievements" : "Recent Achievements"} />
          <Tab label={isMobile ? "Badges" : "All Badges"} />
          <Tab label={isMobile ? "Progress" : "Progress Tracking"} />
          <Tab label="Statistics" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Box>
          <Typography variant="h6" gutterBottom>All Achievements</Typography>
          {allAchievements.length === 0 ? (
            <Alert severity="info">
              Loading achievements...
            </Alert>
          ) : (
            <Box>
              {allAchievements.map(achievement => {
                const userProgress = achievements.find(ua => 
                  ua.achievementType === achievement.name || ua.achievementType === achievement.type
                );
                return (
                  <AchievementProgressCard 
                    key={achievement.id} 
                    achievement={achievement} 
                    userProgress={userProgress}
                  />
                );
              })}
            </Box>
          )}
        </Box>
      )}

      {activeTab === 1 && (
        <Box>
          <Typography variant="h6" gutterBottom>Badge Collection</Typography>
          {allBadges.length === 0 ? (
            <Alert severity="info">
              Loading badges...
            </Alert>
          ) : (
            <Grid container spacing={2}>
              {allBadges.map(badgeDef => {
                const userBadge = badges.find(ub => ub.badgeType === badgeDef.type);
                const isUnlocked = !!userBadge;
                return (
                  <Grid item xs={6} sm={4} md={3} lg={2} key={badgeDef.id}>
                    <BadgeCard badge={badgeDef} isUnlocked={isUnlocked} />
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      )}

      {activeTab === 2 && (
        <Box>
          <Typography variant="h6" gutterBottom>Progress Tracking</Typography>
          
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>Login Streak Progress</Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <LinearProgress 
                  variant="determinate" 
                  value={Math.min((userStats.loginStreak || 0) / 30 * 100, 100)}
                  sx={{ flex: 1, height: 8, borderRadius: 4 }}
                />
                <Typography variant="body2">{userStats.loginStreak || 0}/30 days</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                Next milestone: 30-day streak for Streak Master badge
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>Early Discovery Progress</Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <LinearProgress 
                  variant="determinate" 
                  value={Math.min((userStats.earlyDiscoveries || 0) / 25 * 100, 100)}
                  sx={{ flex: 1, height: 8, borderRadius: 4 }}
                />
                <Typography variant="body2">{userStats.earlyDiscoveries || 0}/25 discoveries</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                Next milestone: 25 discoveries for Hype Hunter badge
              </Typography>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>Movies Watched Progress</Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <LinearProgress 
                  variant="determinate" 
                  value={Math.min((distinctMoviesWatched || 0) / 100 * 100, 100)}
                  sx={{ flex: 1, height: 8, borderRadius: 4 }}
                />
                <Typography variant="body2">{distinctMoviesWatched || 0}/100 movies</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                Next milestone: 100 distinct movies for Binge Watcher achievement
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {activeTab === 3 && (
        <Box>
          <Typography variant="h6" gutterBottom>Detailed Statistics</Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Content Engagement</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><Timeline /></ListItemIcon>
                      <ListItemText 
                        primary="Movies Watched (Distinct)" 
                        secondary={`${distinctMoviesWatched} unique movies`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><Star /></ListItemIcon>
                      <ListItemText 
                        primary="Reviews Written" 
                        secondary={userStats.reviewsCount || 0}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><TrendingUp /></ListItemIcon>
                      <ListItemText 
                        primary="Early Discoveries" 
                        secondary={userStats.earlyDiscoveries || 0}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Social Activity</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><LocalFireDepartment /></ListItemIcon>
                      <ListItemText 
                        primary="Current Streak" 
                        secondary={`${userStats.loginStreak || 0} days`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><EmojiEvents /></ListItemIcon>
                      <ListItemText 
                        primary="Total Points" 
                        secondary={totalPoints}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><WorkspacePremium /></ListItemIcon>
                      <ListItemText 
                        primary="Current Title" 
                        secondary={currentTitle}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}
      </Container>
      
      {/* Achievement Settings Dialog */}
      <AchievementSettings
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        user={user ? { ...user, id: user.userId } : null}
        onUpdate={async (updatedData) => {
          console.log('AchievementSettings updated, refreshing user data:', updatedData);
          // Update user state with new data
          setUser(prev => ({
            ...prev,
            ...updatedData,
            id: prev.id || prev.userId,
            userId: prev.userId
          }));
          // Update auth context directly for immediate badge avatar update
          try {
            await updateUserProfile({
              activeBadges: updatedData.activeBadges,
              badgeDisplaySettings: updatedData.badgeDisplaySettings
            });
          } catch (error) {
            console.error('Error updating user profile:', error);
          }
          // Refresh auth context for badge avatar updates
          refreshUser();
          // Refresh all data
          loadUserData();
        }}
      />
    </Box>
  );
};

export default UserAchievementsDashboard;